import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  LinearProgress,
  Pagination,
  Paper,
  Snackbar,
  SnackbarCloseReason,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Typography,
  useTheme,
  alpha,
  Chip,
  GlobalStyles
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { LayoutBaseDePagina } from '../../../shared/layouts';
import { useDebounce } from '../../../shared/hooks';
import { Environment } from '../../../shared/environment';
import dayjs from 'dayjs';
import { FerramentasDaListagemVendas } from '../../../shared/components/ferramentas-da-listagem/FerramentasDaListagemVendas';
import { IListagemTalao, TalaoService } from '../../../shared/services/api/vendas/Talao/Talao';
import {
  Receipt as ReceiptIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';

export const ListagemDeTalaoPedido: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { debounce } = useDebounce();
  const navigate = useNavigate();
  const theme = useTheme();
  const [rows, setRows] = useState<IListagemTalao[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [openMsg, setOpenMsg] = useState(false);
  const [msg, setMsg] = useState('');
  const [msgType, setMsgType] = useState<'warning' | 'error' | 'info' | 'success'>('warning');

  const handleCloseMsg = (
    event?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') return;
    setOpenMsg(false);
  };

  const tipo = useMemo(() => {
    return searchParams.get('tipo') || 'data'; 
  }, [searchParams]);

  const idfazenda = useMemo(() => {
    return searchParams.get('fazenda') || ''; 
  }, [searchParams]);

  const inicial = useMemo(() => {
    return searchParams.get('inicial') || dayjs().startOf('month').format('DD/MM/YYYY');
  }, [searchParams]);

  const final = useMemo(() => {
    return searchParams.get('final') || dayjs().format('DD/MM/YYYY').toString();
  }, [searchParams]);

  const pagina = useMemo(() => {
    return Number(searchParams.get('pagina') || '1');
  }, [searchParams]);
  
  function verificaFormato(data: string) {
    const regex = /^\d{2}\/\d{2}\/\d{4}$/;
    return regex.test(data);
  }

  useEffect(() => {
    if ( tipo === 'data' && verificaFormato(inicial) && verificaFormato(inicial)) { 
      setIsLoading(true);  
      debounce(() => {
        TalaoService.getAll(pagina, tipo, '', inicial, final, )
          .then((result) => {
            setIsLoading(false);
  
            if (result instanceof Error) {
              setMsgType('error');
              setMsg(result.message);
              setOpenMsg(true);
            } else {
              setTotalCount(result.totalCount);
              setRows(result.data);
            }
          });
      });
    }     
  }, [inicial, final, pagina, tipo]);

  // Função para formatar CPF/CNPJ
  const formatarDocumento = (documento: string) => {
    if (!documento) return '-';

    const apenasNumeros = documento.replace(/\D/g, '');

    if (apenasNumeros.length === 11) {
      return apenasNumeros.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    } else if (apenasNumeros.length === 14) {
      return apenasNumeros.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }

    return documento;
  };

  return (
    <>
      {/* Estilos globais para customizar a barra de scroll */}
      <GlobalStyles
        styles={{
          '*::-webkit-scrollbar': {
            width: '6px',
            height: '6px',
          },
          '*::-webkit-scrollbar-track': {
            background: theme.palette.mode === 'dark'
              ? alpha(theme.palette.background.default, 0.5)
              : '#f1f1f1',
            borderRadius: '3px',
          },
          '*::-webkit-scrollbar-thumb': {
            background: theme.palette.mode === 'dark'
              ? alpha(theme.palette.grey[600], 0.6)
              : alpha(theme.palette.grey[400], 0.6),
            borderRadius: '3px',
            '&:hover': {
              background: theme.palette.mode === 'dark'
                ? alpha(theme.palette.grey[500], 0.8)
                : alpha(theme.palette.grey[500], 0.8),
            }
          }
        }}
      />

      <LayoutBaseDePagina
        titulo='Listagem de Talão Pedido'
        barraDeFerramentas={
          <FerramentasDaListagemVendas
            mostrarInputData
            mostrarInputBasicSelect
            dataInicial={inicial}
            dataFinal={final}
            textoDoSelect={tipo}
            textoBotaoNovo='Adicionar'
            aoClicarEmNovo={() => navigate('/talaopedido/novo')}
            aoMudarDataInicial={inicial => setSearchParams({ inicial: inicial, final: final, tipo: tipo, pagina: '1' }, { replace: true })}
            aoMudarTextoDoSelect={textoselect => setSearchParams({  inicial: inicial, final: final, tipo: textoselect, pagina: '1' }, { replace: true})}
            aoMudarDataFinal={final => setSearchParams({  inicial: inicial, final: final, tipo: tipo, pagina: '1' }, { replace: true })}
            dadosSelecao={[
              { nameBD: 'data', descricao: 'Data' },
            ]}
          />
        }
      >
        <Snackbar
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          open={openMsg}
          autoHideDuration={6000}
          onClose={handleCloseMsg}
        >
          <Alert
            onClose={handleCloseMsg}
            severity={msgType}
            variant='filled'
            sx={{ width: '100%' }}
          >
            {msg}
          </Alert>
        </Snackbar>

        {/* Container principal com margens laterais */}
        <Box sx={{ 
          display: 'flex',
          flexDirection: 'column',
          mx: { xs: 1, sm: 2, md: 2, lg: 2 },
          my: 1
        }}>
          <Paper
            elevation={0}
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 1,
              overflow: 'hidden',
              border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
              backgroundColor: theme.palette.mode === 'dark'
                ? alpha(theme.palette.background.paper, 0.9)
                : theme.palette.background.paper,
            }}
          >
            <TableContainer 
              sx={{ 
                flex: 1,
                overflow: 'auto'
              }}
            >
              <Table 
                size="small" 
                sx={{ 
                  minWidth: '300px',
                  '& .MuiTableCell-root': {
                    whiteSpace: 'nowrap'
                  }
                }}
                stickyHeader // Cabeçalho fixo
              >
                <TableHead>
                  <TableRow sx={{
                    backgroundColor: theme.palette.mode === 'dark'
                      ? alpha(theme.palette.primary.dark, 0.3)
                      : alpha(theme.palette.primary.main, 0.06),
                    '& th': {
                      fontWeight: 'bold',
                      color: theme.palette.mode === 'dark'
                        ? theme.palette.primary.light
                        : theme.palette.primary.dark,
                      fontSize: '0.78rem',
                      py: 1,
                      px: 1.2,
                      borderBottom: `1px solid ${theme.palette.mode === 'dark'
                        ? alpha(theme.palette.primary.light, 0.15)
                        : alpha(theme.palette.primary.main, 0.15)}`,
                      position: 'sticky',
                      top: 0,
                      zIndex: 10
                    }
                  }}>
                    <TableCell sx={{ width: '90px' }}>
                      <Box display="flex" alignItems="center" gap={0.3}>
                        <CalendarIcon fontSize="small" sx={{ fontSize: '16px' }} />
                        <span>Data</span>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ width: '90px' }}>
                      <Box display="flex" alignItems="center" gap={0.3}>
                        <ReceiptIcon fontSize="small" sx={{ fontSize: '16px' }} />
                        <span>Número</span>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={0.3}>
                        <PersonIcon fontSize="small" sx={{ fontSize: '16px' }} />
                        <span>Nome</span>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ width: '130px' }}>
                      <Box display="flex" alignItems="center" gap={0.3}>
                        <AssignmentIcon fontSize="small" sx={{ fontSize: '16px' }} />
                        <span>CPF/CNPJ</span>
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {rows.map((row, index) => (
                    <TableRow
                      key={row.idtalao}
                      hover
                      sx={{
                        cursor: 'pointer',
                        transition: 'all 0.15s ease-in-out',
                        backgroundColor: theme.palette.mode === 'dark'
                          ? (index % 2 === 0
                            ? alpha(theme.palette.background.default, 0.4)
                            : alpha(theme.palette.background.default, 0.6))
                          : (index % 2 === 0
                            ? 'transparent'
                            : alpha(theme.palette.grey[50], 0.3)),
                        '&:hover': {
                          backgroundColor: theme.palette.mode === 'dark'
                            ? alpha(theme.palette.primary.main, 0.12)
                            : alpha(theme.palette.primary.main, 0.03),
                        },
                        '&:last-child td': {
                          borderBottom: 0,
                        },
                        '& td': {
                          py: 0.8,
                          px: 1.2,
                          fontSize: '0.76rem',
                          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                        }
                      }}
                      onClick={() => navigate(`/talaopedido/${row.idtalao}`)}
                    >
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={0.3}>
                          <CalendarIcon color="action" fontSize="small" sx={{ fontSize: '14px' }} />
                          <Typography variant="body2" fontWeight="medium" color="text.primary" fontSize="0.76rem">
                            {dayjs(row.datalan).format('DD/MM/YYYY')}
                          </Typography>
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={row.numero}
                          size="small"
                          color="primary"
                          variant={theme.palette.mode === 'dark' ? 'filled' : 'outlined'}
                          sx={{
                            fontWeight: 'bold',
                            minWidth: '45px',
                            height: '20px',
                            fontSize: '0.7rem',
                            '& .MuiChip-label': {
                              px: 0.8
                            },
                            backgroundColor: theme.palette.mode === 'dark'
                              ? alpha(theme.palette.primary.dark, 0.25)
                              : 'transparent'
                          }}
                        />
                      </TableCell>

                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="medium" color="text.primary" noWrap fontSize="0.76rem">
                            {row.nome || 'Não informado'}
                          </Typography>
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace" color="text.secondary" fontSize="0.76rem">
                          {formatarDocumento(row.cpf || '')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>

                {/* Mensagem de lista vazia */}
                {totalCount === 0 && !isLoading && (
                  <TableBody>
                    <TableRow>
                      <TableCell colSpan={4} align='center' sx={{ py: 6 }}>
                        <Box>
                          <ReceiptIcon sx={{
                            fontSize: 48,
                            color: theme.palette.mode === 'dark'
                              ? alpha(theme.palette.text.secondary, 0.4)
                              : 'text.secondary',
                            mb: 2
                          }} />
                          <Typography variant='h6' color='text.secondary' gutterBottom fontSize="1rem">
                            {Environment.LISTAGEM_VAZIA}
                          </Typography>
                          <Typography variant='body2' color='text.secondary' fontSize="0.8rem">
                            Nenhum talão pedido encontrado no período
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>

            {/* Footer separado para paginação */}
            {(totalCount > 0 && totalCount > Environment.LIMITE_DE_LINHAS) && (
              <Box sx={{
                py: 1.5,
                px: 1.2,
                borderTop: `1px solid ${theme.palette.mode === 'dark'
                  ? alpha(theme.palette.divider, 0.2)
                  : alpha(theme.palette.divider, 0.1)}`,
                backgroundColor: theme.palette.mode === 'dark'
                  ? alpha(theme.palette.background.default, 0.7)
                  : alpha(theme.palette.grey[50], 0.2),
                flexShrink: 0
              }}>
                <Box display='flex' justifyContent='center' alignItems='center'>
                  <Pagination
                    page={pagina}
                    count={Math.ceil(totalCount / Environment.LIMITE_DE_LINHAS)}
                    onChange={(_, newPage) => setSearchParams({  
                      inicial: inicial, 
                      final: final, 
                      tipo: tipo, 
                      pagina: newPage.toString() 
                    }, { replace: true })}
                    color='primary'
                    size="small"
                    shape='rounded'
                    showFirstButton
                    showLastButton
                    sx={{
                      '& .MuiPaginationItem-root': {
                        fontWeight: 'bold',
                        color: theme.palette.text.primary,
                        fontSize: '0.75rem',
                        minWidth: '28px',
                        height: '28px',
                        backgroundColor: theme.palette.mode === 'dark'
                          ? alpha(theme.palette.background.paper, 0.7)
                          : undefined,
                        '&:hover': {
                          backgroundColor: theme.palette.mode === 'dark'
                            ? alpha(theme.palette.primary.main, 0.25)
                            : undefined
                        },
                        '&.Mui-selected': {
                          backgroundColor: theme.palette.mode === 'dark'
                            ? alpha(theme.palette.primary.main, 0.4)
                            : undefined,
                          color: theme.palette.mode === 'dark'
                            ? theme.palette.common.white
                            : undefined
                        }
                      }
                    }}
                  />
                </Box>
              </Box>
            )}

            {/* Loading indicator no footer */}
            {isLoading && (
              <Box sx={{ 
                py: 0.5,
                px: 1.2,
                borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                flexShrink: 0
              }}>
                <LinearProgress
                  sx={{
                    height: '1.5px',
                    backgroundColor: theme.palette.mode === 'dark'
                      ? alpha(theme.palette.primary.dark, 0.2)
                      : undefined,
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: theme.palette.mode === 'dark'
                        ? theme.palette.primary.light
                        : undefined
                    }
                  }}
                />
              </Box>
            )}
          </Paper>
        </Box>
      </LayoutBaseDePagina>
    </>
  );
};